import { Vector2 } from '../../../odie';
import { gsap } from 'gsap';
import { config } from '../../defs/config';
import {
  BlockEntity,
  moveBlockEntity,
  SpiderBlockEntity,
  swapBlockEntities,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { blockIsFrozen } from '../../util/blockTools';
import { mapToViewPosition } from '../../util/mapTools';
import { IEffect } from './IEffect';

export type SpiderEffectOptions = {
  spiderEntities: SpiderBlockEntity[];
};

type SpiderMoveAction = {
  spiderEntity: SpiderBlockEntity;
  targetRow: number;
};

type SpiderMoveUpAnimation = {
  spiderEntity: SpiderBlockEntity;
  blockEntity?: BlockEntity;
  fromRow: number;
  toRow: number;
};

export class SpiderClimbEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly options: SpiderEffectOptions;

  constructor(scene: GameScene, options: SpiderEffectOptions) {
    this.scene = scene;
    this.options = options;
  }

  public static assets(): string[] {
    return [];
  }

  public async execute() {
    const { spiderEntities } = this.options;
    const moveUpActions: SpiderMoveAction[] = [];

    spiderEntities.forEach((spiderEntity: SpiderBlockEntity) => {
      if (
        !spiderEntity.c.blockSpider.movedDown &&
        this.canSpiderMoveUp(spiderEntity)
      ) {
        const distance = this.getMoveUpDistance(spiderEntity);
        moveUpActions.unshift({
          spiderEntity,
          targetRow: spiderEntity.c.position.mapPosition.y - distance,
        });
      }
    });

    if (moveUpActions.length) {
      const upAnimations = this.buildUpAnimations(moveUpActions);

      await Promise.all(
        upAnimations.map((animation) => this.runUpAnimation(animation)),
      );

      return true;
    }

    return false;
  }

  private buildUpAnimations(
    actions: SpiderMoveAction[],
  ): SpiderMoveUpAnimation[] {
    const { map } = this.scene.sessionEntity.c;
    const animations: SpiderMoveUpAnimation[] = [];

    actions.forEach((action) => {
      const { spiderEntity, targetRow } = action;
      const { mapPosition: spiderMapPosition } = spiderEntity.c.position;
      const spiderRow = spiderMapPosition.y;

      const targetPosition = new Vector2(spiderMapPosition.x, targetRow);
      const targetCell = map.getCellAt(targetPosition);
      const targetEntity = targetCell?.base?.entity;

      if (!targetEntity) {
        moveBlockEntity(
          this.scene,
          spiderEntity as BlockEntity,
          targetPosition,
        );
      } else {
        swapBlockEntities(
          this.scene,
          spiderEntity as BlockEntity,
          targetEntity,
        );
      }

      animations.push({
        spiderEntity,
        blockEntity: targetEntity,
        fromRow: spiderRow,
        toRow: targetRow,
      });
    });

    return animations;
  }

  private async runUpAnimation(animation: SpiderMoveUpAnimation) {
    const { spiderEntity, blockEntity } = animation;
    const [spiderViewY, blockViewY] = this.getAnimationYValues(animation);
    const spiderConfig = config.sim.spider;

    spiderEntity.view.y = spiderViewY;

    const spider = spiderEntity.c.blockSpider.view;
    const duration = this.getAnimationDuration(
      blockViewY,
      spiderViewY,
      spiderConfig.moveSpeed,
    );

    const timeline = gsap.timeline().to(spiderEntity.view, {
      y: blockViewY,
      duration,
      ease: 'power2.inOut',
    });

    if (blockEntity) {
      // set swapping block to it's original position if not already animating
      if (!gsap.getTweensOf(blockEntity.view).length) {
        blockEntity.view.y = blockViewY;
      }

      timeline.to(
        blockEntity.view,
        {
          y: spiderViewY,
          duration: this.getAnimationDuration(
            spiderViewY,
            blockViewY,
            spiderConfig.blockPushSpeed,
          ),
          ease: 'quad.inOut',
          overwrite: true,
        },
        0,
      );
    }

    spider.playClimb();
    await timeline;
    spider.playIdle();
  }

  private getAnimationYValues(
    animation: SpiderMoveUpAnimation,
  ): [number, number] {
    const { fromRow, toRow } = animation;
    const { y: fromViewY } = mapToViewPosition(new Vector2(0, fromRow));
    const { y: toViewY } = mapToViewPosition(new Vector2(0, toRow));
    return [fromViewY, toViewY];
  }

  private getAnimationDuration(
    fromY: number,
    toY: number,
    speed: number,
  ): number {
    return Math.abs(toY - fromY) / speed;
  }

  private canSpiderMoveUp(entity: BlockEntity): boolean {
    const { mapPosition } = entity.c.position;
    const { map } = this.scene.sessionEntity.c;

    const aboveRow = mapPosition.y - 1;
    const aboveCell = map.getCellAt({ x: mapPosition.x, y: aboveRow });

    const noCellOrDisabledCell = !aboveCell?.enabled;
    const cellIsFrozen =
      aboveCell?.base && blockIsFrozen(this.scene, aboveCell.base.entity);
    const cellIsImmobile = aboveCell?.base?.entity.c.block.props.immobile;
    const cellHasAnotherSpider =
      aboveCell?.base?.entity.c.block.blockId === 'spider' &&
      !this.canSpiderMoveUp(aboveCell.base.entity as SpiderBlockEntity);

    if (
      noCellOrDisabledCell ||
      cellIsFrozen ||
      cellIsImmobile ||
      cellHasAnotherSpider
    ) {
      return false;
    }

    // no base cell (ie. empty) falls through to height of 1
    const aboveCellHeight = aboveCell.base?.entity.c.block.height || 1;
    return aboveCellHeight === 1;
  }

  private getMoveUpDistance(entity: SpiderBlockEntity) {
    const { map } = this.scene.sessionEntity.c;
    let distance = 0;
    let belowEntity: BlockEntity | undefined = undefined;

    // loop and add to distance until we hit a non-block or a non-spider block.
    do {
      distance++;
      const targetPosition = new Vector2(entity.x, entity.y - distance);
      const targetCell = map.getCellAt(targetPosition);
      if (targetCell?.base?.entity) belowEntity = targetCell.base.entity;
    } while (belowEntity && this.canSpiderMoveUp(belowEntity));

    return distance;
  }
}
